/**
 * **Gestión → Clientes → Mensajes** (`/gestion/clientes/mensajes`).
 *
 * La bandeja que faltaba: hasta ahora el cliente escribía desde su portal
 * (`cliente_mensajes`) y **nadie leía** esos mensajes en el panel. Aquí se ven
 * los hilos con sus filtros, se responde (la respuesta se guarda como mensaje
 * hijo) y se cierra el hilo.
 */

import { AlertTriangle, CheckCheck, Clock, MailOpen, MailPlus, RefreshCw, Search, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateTimeValue } from '@/lib/utils';

import {
  useBandejaMensajes,
  useCerrarMensajeCliente,
  useMarcarMensajeClienteLeido,
  useResponderMensajeCliente,
} from '../mensajes-clientes.api';
import type {
  EstadoMensajeCliente,
  HiloMensajeCliente,
  MensajeCliente,
  PrioridadMensajeCliente,
  TipoMensajeCliente,
} from '../mensajes-clientes.types';

const ETIQUETA_TIPO: Record<TipoMensajeCliente, string> = {
  consulta: 'Consulta',
  reclamo: 'Reclamo',
  solicitud: 'Solicitud',
  respuesta: 'Respuesta',
};

const ETIQUETA_ESTADO: Record<EstadoMensajeCliente, string> = {
  pendiente: 'Pendiente',
  leido: 'Leído',
  respondido: 'Respondido',
  cerrado: 'Cerrado',
};

/** Mensaje legible de un error de la API. */
function mensajeDe(error: unknown, porDefecto: string): string {
  return error instanceof ApiError ? error.message : porDefecto;
}

/** Tono de la etiqueta de prioridad. */
function tonoPrioridad(prioridad: PrioridadMensajeCliente): 'danger' | 'warning' | 'neutral' {
  if (prioridad === 'alta') return 'danger';
  if (prioridad === 'media') return 'warning';

  return 'neutral';
}

/** Tono de la etiqueta de estado. */
function tonoEstado(estado: EstadoMensajeCliente): 'success' | 'brand' | 'neutral' | 'warning' {
  if (estado === 'respondido') return 'success';
  if (estado === 'cerrado') return 'neutral';
  if (estado === 'leido') return 'brand';

  return 'warning';
}

/** Tarjeta de un hilo con sus respuestas. */
function Hilo({
  hilo,
  alResponder,
  alCerrar,
  alMarcarLeido,
}: {
  hilo: HiloMensajeCliente;
  alResponder: (hilo: HiloMensajeCliente) => void;
  alCerrar: (hilo: HiloMensajeCliente) => void;
  alMarcarLeido: (hilo: HiloMensajeCliente) => void;
}) {
  const [abierto, setAbierto] = useState(false);

  return (
    <li className="rounded-card border border-ink-100 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink-900">
            {hilo.asunto}
            {hilo.estado === 'pendiente' ? <Badge variant="warning">nuevo</Badge> : null}
            {hilo.prioridad === 'alta' && hilo.estado !== 'cerrado' ? (
              <Badge variant="danger">
                <AlertTriangle className="mr-1 inline size-3" aria-hidden="true" />
                urgente
              </Badge>
            ) : null}
          </p>
          <p className="mt-0.5 text-xs text-ink-500">
            {hilo.nombre_comercial || hilo.cliente} · <span className="font-mono">{hilo.numero_doc}</span>
            {' · '}
            {ETIQUETA_TIPO[hilo.tipo]} · {formatDateTimeValue(hilo.fecha)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={tonoPrioridad(hilo.prioridad)}>{hilo.prioridad}</Badge>
          <Badge variant={tonoEstado(hilo.estado)}>{ETIQUETA_ESTADO[hilo.estado]}</Badge>
          <Button variant="ghost" size="sm" onClick={() => setAbierto(!abierto)}>
            {abierto ? 'Ocultar' : 'Ver hilo'}
          </Button>
          {hilo.estado === 'pendiente' ? (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<MailOpen className="size-3.5" aria-hidden="true" />}
              onClick={() => alMarcarLeido(hilo)}
            >
              Marcar leído
            </Button>
          ) : null}
          {hilo.estado !== 'cerrado' ? (
            <>
              <Button
                size="sm"
                leftIcon={<Send className="size-3.5" aria-hidden="true" />}
                onClick={() => alResponder(hilo)}
              >
                Responder
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<CheckCheck className="size-3.5" aria-hidden="true" />}
                onClick={() => alCerrar(hilo)}
              >
                Cerrar
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {abierto ? (
        <div className="mt-3 flex flex-col gap-2">
          <div className="rounded-control bg-ink-50/70 p-3">
            <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
              Mensaje del cliente
            </p>
            <p className="mt-1 text-sm whitespace-pre-line text-ink-800">{hilo.mensaje}</p>
          </div>

          {hilo.respuestas.map((respuesta: MensajeCliente) => (
            <div
              key={respuesta.id}
              className="rounded-control border border-brand-100 bg-brand-50/50 p-3"
            >
              <p className="text-[11px] font-semibold tracking-wide text-brand-700 uppercase">
                Respuesta enviada
                {respuesta.respondido_por ? ` · ${respuesta.respondido_por}` : ''}
              </p>
              <p className="mt-1 text-sm whitespace-pre-line text-ink-800">{respuesta.mensaje}</p>
              <p className="mt-1 text-[11px] text-ink-400">
                {formatDateTimeValue(respuesta.fecha)}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </li>
  );
}

export function MensajesClientesPage() {
  useDocumentTitle('Gestión · Mensajes de clientes');
  const [parametros, setParametros] = useSearchParams();

  const filtros = useMemo(
    () => ({
      estado: parametros.get('estado') ?? '',
      prioridad: parametros.get('prioridad') ?? '',
      buscar: parametros.get('buscar') ?? '',
    }),
    [parametros],
  );

  // El buscador escribe en la URL, así que se mantiene un borrador local para no
  // disparar una consulta por tecla.
  const [borrador, setBorrador] = useState(filtros.buscar);

  const consulta = useBandejaMensajes(filtros);
  const responder = useResponderMensajeCliente();
  const cerrar = useCerrarMensajeCliente();
  const marcarLeido = useMarcarMensajeClienteLeido();

  const [respuestaA, setRespuestaA] = useState<HiloMensajeCliente | null>(null);
  const [texto, setTexto] = useState('');
  const [porCerrar, setPorCerrar] = useState<HiloMensajeCliente | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const cambiarFiltro = (campo: string, valor: string) => {
    const siguiente = new URLSearchParams(parametros);

    if (valor) siguiente.set(campo, valor);
    else siguiente.delete(campo);

    setParametros(siguiente, { replace: true });
  };

  const enviarRespuesta = () => {
    if (!respuestaA) return;

    responder.mutate(
      { id: respuestaA.id, mensaje: texto },
      {
        onSuccess: (datos) => {
          mostrar(datos.mensaje);
          setRespuestaA(null);
          setTexto('');
        },
      },
    );
  };

  const confirmarCierre = () => {
    if (!porCerrar) return;

    cerrar.mutate(porCerrar.id, {
      onSuccess: (datos) => {
        mostrar(datos.mensaje);
        setPorCerrar(null);
      },
    });
  };

  const resumen = consulta.data?.resumen;
  const hilos = consulta.data?.hilos ?? [];

  const tarjetas = [
    { etiqueta: 'Pendientes', valor: resumen?.pendientes ?? 0, icono: MailPlus, tono: 'bg-amber-50 text-amber-600' },
    { etiqueta: 'Urgentes', valor: resumen?.urgentes ?? 0, icono: AlertTriangle, tono: 'bg-rose-50 text-rose-600' },
    { etiqueta: 'Respondidos', valor: resumen?.respondidos ?? 0, icono: CheckCheck, tono: 'bg-emerald-50 text-emerald-600' },
    { etiqueta: 'Cerrados', valor: resumen?.cerrados ?? 0, icono: Clock, tono: 'bg-ink-100 text-ink-500' },
  ];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Mensajes de clientes</h1>
          <p className="mt-1 text-sm text-ink-500">
            Consultas, reclamos y solicitudes que los clientes envían desde su portal. Se responden
            aquí y la respuesta aparece en su portal.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          leftIcon={
            <RefreshCw
              className={cn('size-4', consulta.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
          }
          onClick={() => void consulta.refetch()}
        >
          Actualizar
        </Button>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {consulta.error ? (
        <Alert variant="error">
          {mensajeDe(consulta.error, 'No se pudieron cargar los mensajes.')}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {tarjetas.map((tarjeta) => (
          <div key={tarjeta.etiqueta} className="rounded-card border border-ink-100 bg-white p-4">
            <div className="flex items-center gap-2">
              <span className={cn('flex size-8 items-center justify-center rounded-control', tarjeta.tono)}>
                <tarjeta.icono className="size-4" aria-hidden="true" />
              </span>
              <p className="text-xs font-medium text-ink-500">{tarjeta.etiqueta}</p>
            </div>
            <p className="mt-2 text-2xl font-bold text-ink-900">{tarjeta.valor}</p>
          </div>
        ))}
      </div>

      <SectionCard title="Filtros" description="Por estado, prioridad o texto del mensaje.">
        <div className="grid gap-3 lg:grid-cols-3">
          <form
            className="lg:col-span-1"
            onSubmit={(evento) => {
              evento.preventDefault();
              cambiarFiltro('buscar', borrador.trim());
            }}
          >
            <Input
              label="Buscar"
              value={borrador}
              placeholder="Asunto, mensaje, cliente o documento"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              onChange={(evento) => setBorrador(evento.target.value)}
            />
          </form>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="mc-estado" className="text-sm font-medium text-ink-700">
              Estado
            </label>
            <Select
              id="mc-estado"
              value={filtros.estado}
              onChange={(evento) => cambiarFiltro('estado', evento.target.value)}
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendientes</option>
              <option value="leido">Leídos</option>
              <option value="respondido">Respondidos</option>
              <option value="cerrado">Cerrados</option>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="mc-prioridad" className="text-sm font-medium text-ink-700">
              Prioridad
            </label>
            <Select
              id="mc-prioridad"
              value={filtros.prioridad}
              onChange={(evento) => cambiarFiltro('prioridad', evento.target.value)}
            >
              <option value="">Todas</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </Select>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title={`Hilos (${consulta.data?.total ?? 0})`}
        description="Se muestran los últimos 300 mensajes; los urgentes van primero."
      >
        {consulta.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : hilos.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-ink-500">
            <MailOpen className="size-4" aria-hidden="true" />
            No hay mensajes con esos filtros.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {hilos.map((hilo) => (
              <Hilo
                key={hilo.id}
                hilo={hilo}
                alResponder={(elegido) => {
                  setRespuestaA(elegido);
                  setTexto('');
                }}
                alCerrar={setPorCerrar}
                alMarcarLeido={(elegido) =>
                  marcarLeido.mutate(elegido.id, {
                    onSuccess: () => mostrar('Mensaje marcado como leído.'),
                  })
                }
              />
            ))}
          </ul>
        )}
      </SectionCard>

      <Dialog
        open={respuestaA !== null}
        onClose={() => {
          setRespuestaA(null);
          setTexto('');
        }}
        title={respuestaA ? `Responder: ${respuestaA.asunto}` : 'Responder'}
        description={
          respuestaA
            ? `Cliente ${respuestaA.nombre_comercial || respuestaA.cliente} (${respuestaA.numero_doc})`
            : ''
        }
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setRespuestaA(null);
                setTexto('');
              }}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              leftIcon={<Send className="size-3.5" aria-hidden="true" />}
              loading={responder.isPending}
              disabled={texto.trim().length === 0}
              onClick={enviarRespuesta}
            >
              Enviar respuesta
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {respuestaA ? (
            <div className="rounded-control bg-ink-50/70 p-3">
              <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
                El cliente escribió
              </p>
              <p className="mt-1 text-sm whitespace-pre-line text-ink-700">{respuestaA.mensaje}</p>
            </div>
          ) : null}

          <label className="flex flex-col gap-1.5 text-sm font-medium text-ink-700">
            Respuesta
            <textarea
              rows={5}
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
              placeholder="Escribe la respuesta que verá el cliente en su portal."
              className={cn(
                'w-full rounded-control border border-ink-200 bg-white px-4 py-2.5 text-sm text-ink-900 transition-all',
                'placeholder:text-ink-300 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
              )}
            />
          </label>

          {responder.error ? (
            <Alert variant="error">
              {mensajeDe(responder.error, 'No se pudo enviar la respuesta.')}
            </Alert>
          ) : null}
        </div>
      </Dialog>

      <ConfirmDialog
        open={porCerrar !== null}
        title="Cerrar el hilo"
        description={
          porCerrar
            ? `«${porCerrar.asunto}» quedará cerrado: el cliente lo seguirá viendo, pero ya no se podrá responder desde aquí.`
            : ''
        }
        confirmLabel="Cerrar hilo"
        loading={cerrar.isPending}
        onConfirm={confirmarCierre}
        onClose={() => setPorCerrar(null)}
      />
    </div>
  );
}
